import { useCopy } from '../../content'
import type { ServiceKey } from '../../content/types'
import { SERVICE_MEDIA_ROLE } from '../../data/services'
import { Picture } from '../media/Picture'
import { Breadcrumbs } from './Breadcrumbs'

/**
 * Photo hero of a service page, with the page's one `<h1 tabIndex={-1}>`.
 *
 * The picture is the page's LCP image and head.ts already preloads its AVIF
 * with `imagesizes="100vw"`, so `sizes` here must stay `100vw` (a mismatch
 * would fetch a second rendition).
 *
 * Contrast is held by the veil alone, whatever the photo (same recipe as the
 * zone page): ≥ black/60 down to 6rem above the bottom — white/85 text stays
 * ≥ 4.6:1 even over a pure-white pixel — and only the last 6rem, below every
 * line of text (`pb-36`), fades into the paper band that follows.
 */
export function ServiceHero({ serviceKey }: { serviceKey: ServiceKey }) {
  const page = useCopy().pages[serviceKey]
  return (
    <section className="relative overflow-hidden text-white">
      <Picture role={SERVICE_MEDIA_ROLE[serviceKey]} alt={page.seo.ogAlt} sizes="100vw" priority fill className="absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/60 via-[calc(100%-6rem)] to-vc2-paper" />
      <div className="relative vc2-container pt-32 pb-36 md:pt-40 md:pb-44">
        <Breadcrumbs current={page.name} />
        <p className="vc2-kicker mt-8 text-white/85">{page.name}</p>
        <h1
          tabIndex={-1}
          className="vc2-display mt-4 max-w-4xl text-[2.125rem] leading-[1.05] sm:text-5xl md:text-6xl"
        >
          {page.heroTitle}
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-white/90">{page.heroIntro}</p>
      </div>
    </section>
  )
}
